import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, ZoomControl, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import styles from './Map.module.scss';
import { CENTER_POSITION, DEFAULT_ZOOM, MIN_ZOOM, ZOOM_CONTROL_POSITION } from './helpers';

export function DRVehicleTrackerWrapper({
  markers,
  popupContent,
  customMarkerRenderer,
  children,
  onMapReady
}) {
  const mapRef = useRef(null);

  return (
    <div className={styles.map_wrapper}>
      <MapContainer
        className={styles.map}
        tap={false}
        zoomControl={false}
        center={CENTER_POSITION}
        zoom={DEFAULT_ZOOM}
        minZoom={MIN_ZOOM}
        whenCreated={(mapInstance) => {
          mapRef.current = mapInstance;

          // Create custom panes
          mapInstance.createPane('markerPane');
          mapInstance.getPane('markerPane').style.zIndex = 600;

          mapInstance.createPane('clusterPane');
          mapInstance.getPane('clusterPane').style.zIndex = 500;

          if (onMapReady) onMapReady(mapInstance);
        }}>
        <TileLayer
          attribution="Google Maps"
          url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&hl=en"
        />

        <ZoomControl position={ZOOM_CONTROL_POSITION} />

        <CustomClusterMarkers
          markers={markers}
          popupContent={popupContent}
          renderMarker={customMarkerRenderer}
        />
      </MapContainer>

      {children}
    </div>
  );
}

function CustomClusterMarkers({ markers, renderMarker, popupContent }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    const clusterGroup = L.markerClusterGroup({
      pane: 'clusterPane',
      disableClusteringAtZoom: 17,
      maxClusterRadius: 80, 
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      removeOutsideVisibleBounds: true,
      chunkedLoading: true,
      iconCreateFunction: function (cluster) {
        const count = cluster.getChildCount();

        // Decide cluster color based on count
        let backgroundColor = '#ffcc00'; // default (yellow)
        if (count >= 10 && count < 50) backgroundColor = '#ffa500'; // orange
        else if (count >= 50) backgroundColor = '#ff4500'; // red-orange

        return L.divIcon({
          html: `
              <div style="
                background: ${backgroundColor};
                color: black;
                border-radius: 50%;
                width: 36px;
                height: 36px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 13px;
                font-weight: bold;
                border: 2px solid white;
                box-shadow: 0 0 4px rgba(0,0,0,0.25);
              ">
                ${count}
              </div>
            `,
          className: 'custom-cluster-icon',
          iconSize: [36, 36]
        });
      }
    });

    // Add markers
    markers.forEach((marker) => {
      const leafletMarker = renderMarker(marker, map, popupContent);
      if (leafletMarker) {
        clusterGroup.addLayer(leafletMarker);
      }
    });

    map.addLayer(clusterGroup);

    return () => {
      map.removeLayer(clusterGroup);
    };
  }, [map, markers, renderMarker, popupContent]);

  return null;
}
